import React from 'react';
import {
  formatPercentageES,
  formatPeriodES,
  translateComponent,
  translateIndicator,
  TABLE_HEADERS_ES
} from '../../../config/reportTranslations';
import './Anexo1.css';

/**
 * Anexo 1: Historical Financial Indicators Table
 *
 * Comprehensive table showing all CAMEL indicators across historical periods
 * Grouped by component (C, EG, L, R, S, STI)
 */

export interface HistoricalIndicatorData {
  period: string;              // YYYY-MM format
  component: string;           // C, EG, L, R, S, STI
  indicator_id: string;        // e.g., "C1", "L2", "R3"
  indicator_name: string;      // Spanish name
  indicator_value: number;     // Raw value
  indicator_score: number;     // Calculated score (0-100)
}

export interface Anexo1Props {
  data: HistoricalIndicatorData[];
  title?: string;
}

// Component order for display
const COMPONENT_ORDER = ['C', 'S', 'L', 'EG', 'R', 'STI'];

export const Anexo1: React.FC<Anexo1Props> = ({
  data,
  title = 'Anexo 1: Indicadores Financieros Históricos'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="anexo-1-container">
        <h3 className="anexo-1-title">{title}</h3>
        <p className="anexo-1-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Get unique periods and sort (newest first)
  const periods = Array.from(new Set(data.map(d => d.period)))
    .sort((a, b) => b.localeCompare(a))
    .slice(0, 9); // Show up to 9 periods

  // Group data by component and indicator
  const groupedData = COMPONENT_ORDER.reduce((acc, componentCode) => {
    const componentData = data.filter(d => d.component === componentCode);

    // Get unique indicators for this component
    const indicators = Array.from(
      new Set(componentData.map(d => d.indicator_id))
    ).sort();

    acc[componentCode] = indicators.map(indicatorId => {
      const indicatorData = componentData.filter(d => d.indicator_id === indicatorId);
      const indicatorName = indicatorData[0]?.indicator_name || translateIndicator(indicatorId);

      // Create period map for this indicator
      const periodValues: Record<string, number | null> = {};
      periods.forEach(period => {
        const periodData = indicatorData.find(d => d.period === period);
        periodValues[period] = periodData ? periodData.indicator_value : null;
      });

      return {
        indicator_id: indicatorId,
        indicator_name: indicatorName,
        values: periodValues
      };
    });

    return acc;
  }, {} as Record<string, Array<{ indicator_id: string; indicator_name: string; values: Record<string, number | null> }>>);

  // Format value based on indicator type (most are percentages)
  const formatValue = (value: number | null): string => {
    if (value === null) return '-';
    // Most indicators are ratios/percentages, multiply by 100
    return formatPercentageES(value * 100);
  };

  return (
    <div className="anexo-1-container">
      <h3 className="anexo-1-title">{title}</h3>

      <div className="anexo-1-table-wrapper">
        <table className="anexo-1-table">
          <thead>
            <tr>
              <th className="anexo-1-header-component" rowSpan={2}>Componente</th>
              <th className="anexo-1-header-indicator" rowSpan={2}>Indicador</th>
              <th className="anexo-1-header-periods" colSpan={periods.length}>
                Períodos
              </th>
            </tr>
            <tr>
              {periods.map(period => (
                <th key={period} className="anexo-1-header-period">
                  {formatPeriodES(period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPONENT_ORDER.map(componentCode => {
              const componentIndicators = groupedData[componentCode];
              if (!componentIndicators || componentIndicators.length === 0) return null;

              const componentName = translateComponent(componentCode);

              return (
                <React.Fragment key={componentCode}>
                  {componentIndicators.map((indicator, idx) => (
                    <tr key={`${componentCode}-${indicator.indicator_id}`}>
                      {/* Component name - only show for first indicator */}
                      {idx === 0 && (
                        <td
                          className="anexo-1-cell-component"
                          rowSpan={componentIndicators.length}
                        >
                          {componentName}
                        </td>
                      )}

                      {/* Indicator name */}
                      <td className="anexo-1-cell-indicator">
                        <span className="anexo-1-indicator-id">{indicator.indicator_id}</span>
                        {' - '}
                        <span className="anexo-1-indicator-name">{indicator.indicator_name}</span>
                      </td>

                      {/* Values for each period */}
                      {periods.map(period => (
                        <td key={period} className="anexo-1-cell-value">
                          {formatValue(indicator.values[period])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Note */}
      <p className="anexo-1-note">
        Nota: Los valores se presentan como porcentajes. "-" indica datos no disponibles para el período.
      </p>

      {/* Source note */}
      <p className="anexo-1-source">
        Fuente: Estados financieros de la institución reportados a la Superintendencia
      </p>
    </div>
  );
};

export default Anexo1;
